import { useRef } from "react";
import { ArrowLeft, Reply, ReplyAll, Forward, Trash2, Paperclip, Download, MailOpen, Mail, Printer } from "lucide-react";
import { toast } from "sonner";
import { SenderAvatar } from "./MailAvatar";
import { InlineReply } from "./InlineReply";

const fmtDate = (d) =>
  d ? new Date(d).toLocaleString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "";

const fmtSize = (n) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

const who = (a) => (a?.name ? `${a.name} <${a.email}>` : a?.email || "");

const esc = (s) => (s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

export const MessageView = ({ api, msg, onBack, onReply, onReplyAll, onForward, onDelete, onToggleSeen, busy, activeEmail, onSent, onUndoableSend }) => {
  const iframeRef = useRef(null);

  const resize = () => {
    const f = iframeRef.current;
    if (f?.contentDocument) {
      f.style.height = Math.min(Math.max(f.contentDocument.documentElement.scrollHeight + 24, 160), 4000) + "px";
    }
  };

  const print = () => {
    const el = document.createElement("div");
    el.className = "mail-print-area";
    el.innerHTML =
      `<h1 style="font-size:20px;margin:0 0 10px;">${esc(msg.subject)}</h1>` +
      `<div style="font-size:12px;color:#444;line-height:1.7;border-bottom:1px solid #ccc;padding-bottom:10px;margin-bottom:16px;">` +
      `<div><b>From:</b> ${esc(who(msg.from))}</div>` +
      `<div><b>To:</b> ${esc(msg.to.map(who).join(", ") || "—")}</div>` +
      (msg.cc?.length ? `<div><b>Cc:</b> ${esc(msg.cc.map(who).join(", "))}</div>` : "") +
      `<div><b>Date:</b> ${esc(fmtDate(msg.date))}</div></div>` +
      (msg.html || `<pre style="white-space:pre-wrap;font-family:inherit;font-size:14px;">${esc(msg.text || "(empty message)")}</pre>`);
    document.body.appendChild(el);
    const cleanup = () => {
      el.remove();
      window.removeEventListener("afterprint", cleanup);
    };
    window.addEventListener("afterprint", cleanup);
    window.print();
    setTimeout(cleanup, 3000);
  };

  const download = async (att) => {
    try {
      const res = await api.get(`/mail/message/${msg.uid}/attachment/${att.index}`, {
        params: { folder: msg.folder },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = att.filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download attachment");
    }
  };

  const link = "flex items-center gap-1.5 text-[13px] text-ol-blue hover:bg-ol-hover rounded-[4px] px-2.5 py-1.5 transition-colors";

  return (
    <div className="flex flex-col h-full" data-testid="mail-message-view">
      {/* subject bar */}
      <div className="flex items-center gap-2 px-5 py-3 border-b border-ol-line shrink-0">
        <button onClick={onBack} className="md:hidden flex items-center gap-1.5 text-[13px] text-ol-sub hover:text-ol-text pr-2" data-testid="mail-back-button"><ArrowLeft size={16} /> Back</button>
        <h2 className="text-[19px] font-semibold text-ol-text leading-snug truncate flex-1" data-testid="mail-subject">{msg.subject}</h2>
        <button onClick={print} className="p-2 text-ol-sub hover:bg-ol-hover rounded-[4px] transition-colors hidden sm:inline-flex" title="Print" data-testid="mail-print-button"><Printer size={16} /></button>
        <button onClick={() => onToggleSeen(!msg.seen)} className="p-2 text-ol-sub hover:bg-ol-hover rounded-[4px] transition-colors" title={msg.seen ? "Mark unread" : "Mark read"} data-testid="mail-toggleseen-button">{msg.seen ? <Mail size={16} /> : <MailOpen size={16} />}</button>
        <button onClick={onDelete} disabled={busy} className="p-2 text-ol-sub hover:bg-ol-hover hover:text-red-500 rounded-[4px] transition-colors disabled:opacity-50" title="Delete" data-testid="mail-delete-button"><Trash2 size={16} /></button>
      </div>

      <div className="flex-1 overflow-y-auto px-5 sm:px-8 py-5">
        {/* sender row */}
        <div className="flex items-start gap-3">
          <SenderAvatar email={msg.from?.email} name={msg.from?.name} size={40} />
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-[15px] font-semibold text-ol-text truncate" data-testid="mail-from">{msg.from?.name || msg.from?.email}</span>
              <span className="text-[12px] text-ol-sub shrink-0">{fmtDate(msg.date)}</span>
            </div>
            {msg.from?.name && <p className="text-[12px] text-ol-sub truncate">{msg.from?.email}</p>}
            <p className="mt-0.5 text-[12px] text-ol-sub truncate">
              To: {msg.to.map(who).join(", ") || "—"}
              {msg.cc?.length > 0 && <> · Cc: {msg.cc.map(who).join(", ")}</>}
            </p>
          </div>
        </div>

        {/* action links */}
        <div className="mt-3 flex flex-wrap gap-1">
          <button onClick={onReply} className={link} data-testid="mail-reply-button"><Reply size={15} /> Reply</button>
          <button onClick={onReplyAll} className={link} data-testid="mail-replyall-button"><ReplyAll size={15} /> Reply all</button>
          <button onClick={onForward} className={link} data-testid="mail-forward-button"><Forward size={15} /> Forward</button>
        </div>

        {msg.attachments.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2" data-testid="mail-attachments">
            {msg.attachments.map((att) => (
              <button key={att.index} onClick={() => download(att)} className="flex items-center gap-2 text-[12px] text-ol-text border border-ol-line hover:border-ol-blue bg-ol-bg px-3 py-2 rounded-[4px] transition-colors" data-testid={`mail-attachment-${att.index}`}>
                <Paperclip size={14} className="text-ol-blue" />
                {att.filename}
                <span className="text-ol-sub">{fmtSize(att.size)}</span>
                <Download size={13} className="text-ol-sub" />
              </button>
            ))}
          </div>
        )}

        <div className="mt-5 border-t border-ol-line pt-5">
          {msg.html ? (
            <iframe
              ref={iframeRef}
              title="Email content"
              sandbox="allow-same-origin allow-popups"
              onLoad={resize}
              srcDoc={`<!DOCTYPE html><html><head><style>body{font-family:"Segoe UI",Arial,sans-serif;font-size:14px;color:#242424;margin:0;padding:4px;}img{max-width:100%;height:auto;}a{color:#0F6CBD;}</style></head><body>${msg.html}</body></html>`}
              className="w-full bg-white border-0"
              style={{ minHeight: 160 }}
              data-testid="mail-html-frame"
            />
          ) : (
            <pre className="whitespace-pre-wrap break-words text-[14px] text-ol-text leading-relaxed" style={{ fontFamily: '"Segoe UI", Arial, sans-serif' }} data-testid="mail-text-body">{msg.text || "(empty message)"}</pre>
          )}
        </div>

        <div className="mt-4 border-t border-ol-line pt-3">
          <InlineReply api={api} message={msg} activeEmail={activeEmail} onForward={() => onForward()} onSent={onSent} onUndoableSend={onUndoableSend} />
        </div>
      </div>
    </div>
  );
};
